// Theme System
// Comprehensive theme configuration for light and dark modes

import { colors, semanticColors, darkColors } from './colors';
import { typography, typographyStyles } from './typography';
import { spacing, borderRadius, shadows, zIndex, transitions, componentSpacing } from './spacing';
import { componentTokens, componentSizes } from './components';

export interface Theme {
  colors: typeof colors;
  semanticColors: any; // Allow flexible color overrides for themes
  typography: typeof typography;
  typographyStyles: typeof typographyStyles;
  spacing: typeof spacing;
  componentSpacing: typeof componentSpacing;
  borderRadius: typeof borderRadius;
  shadows: any; // Allow shadow overrides
  zIndex: typeof zIndex;
  transitions: typeof transitions;
  componentTokens: any; // Allow component token overrides
  componentSizes: typeof componentSizes;
  mode: 'light' | 'dark';
}

// Light Theme Configuration
export const lightTheme: Theme = {
  colors,
  semanticColors,
  typography,
  typographyStyles,
  spacing,
  componentSpacing,
  borderRadius,
  shadows,
  zIndex,
  transitions,
  componentTokens,
  componentSizes,
  mode: 'light'
};

// Dark Theme Configuration
export const darkTheme: Theme = {
  colors,
  semanticColors: {
    ...semanticColors,
    ...darkColors
  },
  typography,
  typographyStyles,
  spacing,
  componentSpacing,
  borderRadius,
  shadows: {
    ...shadows,
    // Enhanced shadows for dark mode
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.2)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
  },
  zIndex,
  transitions,
  componentTokens: {
    ...componentTokens,
    // Update component tokens for dark mode
    button: {
      ...componentTokens.button,
      secondary: {
        ...componentTokens.button.secondary,
        background: darkColors.surface.primary,
        backgroundHover: darkColors.surface.hover,
        backgroundActive: darkColors.surface.active,
        text: darkColors.text.primary,
        border: darkColors.border.primary,
        borderHover: darkColors.border.hover
      }
    },
    card: {
      ...componentTokens.card,
      background: darkColors.surface.primary,
      border: darkColors.border.primary,
      borderHover: darkColors.border.hover
    },
    form: {
      ...componentTokens.form,
      input: {
        ...componentTokens.form.input,
        background: darkColors.surface.primary,
        backgroundDisabled: darkColors.surface.disabled,
        border: darkColors.border.primary,
        borderHover: darkColors.border.hover,
        text: darkColors.text.primary,
        textDisabled: darkColors.text.disabled,
        placeholder: darkColors.text.tertiary
      },
      label: {
        ...componentTokens.form.label,
        text: darkColors.text.primary
      }
    },
    navigation: {
      ...componentTokens.navigation,
      sidebar: {
        ...componentTokens.navigation.sidebar,
        background: darkColors.surface.primary,
        border: darkColors.border.primary
      },
      item: {
        ...componentTokens.navigation.item,
        backgroundHover: darkColors.surface.hover,
        text: darkColors.text.secondary,
        textHover: darkColors.text.primary
      }
    }
  },
  componentSizes,
  mode: 'dark'
};

// Default theme
export const defaultTheme = lightTheme;